import { t } from "@hakili/i18n";
import { Button, Card, Field } from "@hakili/ui";
import { useCallback, useEffect, useState } from "react";

/**
 * `P0-CNW-002`, round B of dealer onboarding: **the dealership's fiche names its
 * administrator, and the console SENDS the invitation.**
 *
 * `POST /organisations/:id/first-admin` has existed for rounds. What never
 * existed is a screen, so the only way to call it was a terminal , and the
 * founder's rule of the walk, `00-SPINE` 6.1, says a step that needs the
 * terminal is a step not yet built. This is that step, built.
 *
 * ## Three outcomes, and each says a different thing
 *
 * **Refused** , the API declined; its own sentence is shown, because *"cette
 * organisation a déjà un responsable"* tells an operator what to do and a
 * generic failure does not.
 *
 * **Created and sent** , the account and the invitation exist and the mail
 * left. The address is NAMED back, because an operator who mistypes it must be
 * able to see where the invitation actually went; otherwise the mistake is
 * invisible until nobody arrives.
 *
 * **Created and NOT sent** , `P0-MSG-006` commits the invitation and then
 * dispatches, so a provider refusal leaves a real invitation that only needs
 * carrying again. This is the case « Renvoyer » exists for, and it must not be
 * confused with the first: nothing needs creating twice, and a second
 * `first-admin` would refuse.
 */

/**
 * The api's four states, and the sentence each becomes , the SAME map the
 * dealerships list renders, because a state worded two ways on two screens is a
 * product that looks assembled by two companies.
 */
const STATE_LABEL: Readonly<Record<string, string>> = {
  active: "console.dealerships.stateActive",
  invitationPending: "console.dealerships.stateInvitationPending",
  invitationRevoked: "console.dealerships.stateInvitationRevoked",
  deactivated: "console.dealerships.stateDeactivated",
};

export interface FicheConcessionProps {
  readonly concession: { readonly id: string; readonly nom: string };
  /**
   * `P0-CNW-030`: **what the door already knows about this dealership's
   * administrator**, or `null` when it has none.
   *
   * The founder's walk of 5 September, step 2: this fiche rendered the naming
   * form for a dealership whose administrator was named and waiting, and
   * `POST /organisations/:id/first-admin` refused the gesture in words , « Cette
   * organisation a déjà son premier responsable ». **A screen that offers a
   * gesture the api will refuse is a screen that has not read.** It reads now,
   * from the row of the list the founder clicked (`A50`), and offers the naming
   * form only where there is nobody to name.
   */
  readonly administrateur: {
    readonly fullName: string;
    readonly state: string;
    readonly invitationId: string | null;
  } | null;
  readonly nommerAdministrateur: (saisie: { fullName: string; email: string }) => Promise<
    /** `E-1`, `M2`: `mailStatus` is the row's own word; `envoye` stays for a build meeting an older answer. */
    { ok: true; invitationId: string; envoye?: boolean; mailStatus?: string } | { ok: false; message: string }
  >;
  readonly renvoyer: (
    invitationId: string,
  ) => Promise<{ ok: true; envoye: boolean } | { ok: false; message: string }>;
  /**
   * `P0-PTA-001`: **where this dealership is**, read through partnerships' own
   * door and never from a column on the organisation , 05 §1950 gives a
   * partner's address one source.
   *
   * The fiche is where the founder sets it, on the walk, for the four
   * dealerships that have none.
   */
  readonly lireAdresse: (
    organisationId: string,
  ) => Promise<
    | { ok: true; adresse: { ville: string | null; quartier: string | null; telephone: string | null } }
    | { ok: false; message: string }
  >;
  /** `R64`, rule one: the covered cities, so the city is chosen and never typed. */
  readonly lireVilles: () => Promise<readonly { id: string; name: string }[]>;
  /** `R64`, rule three: the places of ONE city. */
  readonly lireLieux: (cityId: string) => Promise<readonly { code: string; label: string }[]>;
  readonly ecrireAdresse: (
    organisationId: string,
    saisie: { ville: string; quartier: string; telephone: string },
  ) => Promise<{ ok: true } | { ok: false; message: string }>;
}

type Etat =
  | { readonly phase: "repos" }
  | { readonly phase: "envoye"; readonly email: string }
  | { readonly phase: "aRenvoyer"; readonly email: string; readonly invitationId: string }
  /**
   * `E-1`, `M2`: **the word a boolean could never say.** The row is written, nobody could send it
   * yet, and the job still owes it , neither « envoyée » (which would be the defect `« Renvoyer »
   * reported a delivery that never happened` cured) nor « pas parti » (which would report a failure
   * that has not happened either). « Renvoyer » stays offered here, as it is on `echec`.
   */
  | { readonly phase: "enAttente"; readonly email: string; readonly invitationId: string };

export function FicheConcession({
  concession,
  administrateur,
  lireAdresse,
  ecrireAdresse,
  lireVilles,
  lireLieux,
  nommerAdministrateur,
  renvoyer,
}: FicheConcessionProps): React.JSX.Element {
  const [nom, setNom] = useState("");
  const [email, setEmail] = useState("");
  const [etat, setEtat] = useState<Etat>({ phase: "repos" });
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  /**
   * `P0-PTA-001`: the address, read from partnerships' door on mount.
   *
   * `null` while the read is in flight, and a value with `ville === null`
   * afterwards for a dealership nobody has addressed , the two are different
   * states and the screen renders them differently. Collapsing them would show
   * « Adresse à compléter » for a quarter of a second on every fiche, including
   * the ones that have an address.
   */
  const [adresse, setAdresse] = useState<{
    ville: string | null;
    quartier: string | null;
    telephone: string | null;
  } | null>(null);
  const [editionAdresse, setEditionAdresse] = useState(false);
  const [ville, setVille] = useState("");
  const [quartier, setQuartier] = useState("");
  const [telephone, setTelephone] = useState("");
  const [addressError, setAddressError] = useState<string | null>(null);
  const [villes, setVilles] = useState<readonly { id: string; name: string }[]>([]);
  const [lieux, setLieux] = useState<readonly { code: string; label: string }[]>([]);

  /**
   * `round F2`, found by CI on a round that never touched this file: **three
   * effects wrote state after the component had gone.**
   *
   * Each one awaits a door and then calls a setter with nothing checking that
   * the screen is still mounted. React reaches for `window` on the update, so
   * in a test process whose jsdom environment has already been torn down the
   * rejection is `window is not defined` , which passed locally, failed on
   * every CI run of this head, and named a file this round had not opened.
   *
   * **It is a real defect and not a test artefact.** A console operator who
   * closes a dealership's card while its address is still loading is in
   * exactly the state the suite reproduced; React logs the update and the work
   * is wasted. The cure is the one `dealer-web` already uses in the same shape:
   * a flag the cleanup clears, so a settled promise for a screen nobody is
   * looking at does nothing at all.
   */
  const loadAddress = useCallback(
    async (alive: () => boolean = () => true): Promise<void> => {
      const answer = await lireAdresse(concession.id);
      if (!alive()) return;
      if (answer.ok) {
        setAdresse(answer.adresse);
        setAddressError(null);
      } else {
        setAddressError(answer.message);
      }
    },
    [lireAdresse, concession.id],
  );

  useEffect(() => {
    const mounted = { yes: true };
    void loadAddress(() => mounted.yes);
    return () => {
      mounted.yes = false;
    };
  }, [loadAddress]);

  useEffect(() => {
    // A HOLDER and not a `let`. TypeScript narrows a `let alive = true` to the
    // literal `true` inside the closure , it does not model the cleanup's
    // assignment , so the guard reads as always-truthy and
    // `no-unnecessary-condition` says so, correctly, about a condition that is
    // not unnecessary at all. A property read is not narrowed that way.
    const mounted = { yes: true };
    void (async () => {
      const answer = await lireVilles();
      if (mounted.yes) setVilles(answer);
    })();
    return () => {
      mounted.yes = false;
    };
  }, [lireVilles]);

  /**
   * `R64`, rule three: **the quartier never overrides the city.** The places
   * offered are the CHOSEN city's own, so a Bamako quartier cannot be reached
   * from Segou , which is exactly what re-routed a dealership on the walk.
   */
  const villeChoisie = villes.find((entry) => entry.name === ville);
  useEffect(() => {
    if (villeChoisie === undefined) {
      setLieux([]);
      return;
    }
    const mounted = { yes: true };
    void (async () => {
      const answer = await lireLieux(villeChoisie.id);
      if (mounted.yes) setLieux(answer);
    })();
    return () => {
      mounted.yes = false;
    };
  }, [villeChoisie, lireLieux]);
  const choisitLeQuartier = lieux.length > 1;

  const saveAddress = async (): Promise<void> => {
    const answer = await ecrireAdresse(concession.id, {
      ville: ville.trim(),
      quartier: quartier.trim(),
      telephone: telephone.trim(),
    });
    if (!answer.ok) {
      // The door's own sentence. The form stays open holding what was typed:
      // a refusal that closes the form makes a person retype an address to
      // correct one word of it.
      setAddressError(answer.message);
      return;
    }
    setAddressError(null);
    setEditionAdresse(false);
    await loadAddress();
  };

  const nommer = async (): Promise<void> => {
    if (enCours) return;
    setErreur(null);
    if (nom.trim() === "" || !email.includes("@")) {
      setErreur(t("console.fiche.erreurChamps"));
      return;
    }
    setEnCours(true);
    const resultat = await nommerAdministrateur({ fullName: nom.trim(), email: email.trim() });
    setEnCours(false);
    if (!resultat.ok) {
      setErreur(resultat.message);
      return;
    }
    // `E-1`, `M2`: **the row's own word decides, and the boolean is the fallback.**
    //
    // `mailStatus` is what `identity` now answers , `envoye` and only `envoye` means the mail left,
    // `aEnvoyer` means the job still owes it, `echec` means it did not go and says why. A build
    // meeting an older answer still reads `envoye`, and an API that said NEITHER is read as it
    // always was: the route answers 201 whether or not the mail left, and the console must not
    // invent a failure it did not report.
    const enCoursDEnvoi = resultat.mailStatus === "aEnvoyer";
    const parti =
      resultat.mailStatus === undefined ? resultat.envoye !== false : resultat.mailStatus === "envoye";
    setEtat(
      enCoursDEnvoi
        ? { phase: "enAttente", email: email.trim(), invitationId: resultat.invitationId }
        : parti
          ? { phase: "envoye", email: email.trim() }
          : { phase: "aRenvoyer", email: email.trim(), invitationId: resultat.invitationId },
    );
  };

  /**
   * One resend path, taking the id explicitly.
   *
   * It was reachable only from the `aRenvoyer` phase , a send that did not
   * leave, IN THIS TAB. The fiche now also reaches it from the door's own
   * reading on a fresh page load, which is the case the founder was in, so the
   * id is an argument rather than a phase's field.
   */
  const resendThisLink = async (invitationId: string): Promise<void> => {
    if (enCours) return;
    setEnCours(true);
    const resultat = await renvoyer(invitationId);
    setEnCours(false);
    if (resultat.ok) {
      // `P0-IDN-062`: a resend that revoked the old link and carried nothing is
      // not a success, and saying it was would leave the invitee with no
      // working link at all. « Renvoyer » stays offered.
      if (!resultat.envoye) {
        setErreur(t("console.fiche.renvoiNonParti"));
        return;
      }
      setEtat({ phase: "envoye", email: etat.phase === "repos" ? email.trim() : etat.email });
      setErreur(null);
      return;
    }
    setErreur(resultat.message);
  };

  /**
   * The state as the api words it, mapped once. An unknown code renders as
   * ITSELF rather than as a default sentence , printing a state the api never
   * said is how a screen comes to describe a system it has not read.
   */
  const stateLabel =
    administrateur === null ? null : (STATE_LABEL[administrateur.state] ?? administrateur.state);

  // **The resend is offered on the DOOR's reading**, not on what happened in
  // this tab: `invitationId` is `null` once the invitation is accepted or
  // revoked, so « Renvoyer » appears exactly while there is something live to
  // resend. The `aRenvoyer` phase below still offers it after a send that did
  // not leave, which is the case within one page load.
  const invitationEnCours = administrateur?.invitationId ?? null;

  /**
   * The address block, built once and rendered on both branches.
   *
   * Three states, and they are three rather than two on purpose: **in flight**
   * renders nothing, **absent** says « Adresse à compléter » in words, and
   * **known** shows it. Collapsing the first two would flash "not set" on every
   * fiche that has an address, for as long as the read takes.
   */
  const blocAdresse =
    adresse === null ? null : editionAdresse ? (
      <div className="mb-4 space-y-3" data-testid="fiche-adresse-formulaire">
        <Field label={t("console.fiche.ville")} htmlFor="fiche-ville">
          <select
            id="fiche-ville"
            value={ville}
            onChange={(event) => {
              // Changing the city clears the quartier: one chosen for another
              // city means nothing here, and keeping it is how the two disagree.
              setVille(event.target.value);
              setQuartier("");
            }}
            className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
            data-testid="fiche-ville"
          >
            <option value="">{t("console.fiche.villeChoisir")}</option>
            {villes.map((entry) => (
              <option key={entry.id} value={entry.name}>
                {entry.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t("console.fiche.quartier")} htmlFor="fiche-quartier">
          {choisitLeQuartier ? (
            <select
              id="fiche-quartier"
              value={quartier}
              onChange={(event) => {
                setQuartier(event.target.value);
              }}
              className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
              data-testid="fiche-quartier"
            >
              <option value="">{t("console.fiche.quartierChoisir")}</option>
              {lieux.map((lieu) => (
                <option key={lieu.code} value={lieu.label}>
                  {lieu.label}
                </option>
              ))}
            </select>
          ) : (
            // Outside Bamako the city has one zone and the quartier is the text
            // somebody types , those cities have no quartier list, and refusing
            // free text there would refuse the address itself.
            <input
              id="fiche-quartier"
              value={quartier}
              onChange={(event) => {
                setQuartier(event.target.value);
              }}
              className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
              data-testid="fiche-quartier"
            />
          )}
        </Field>
        <Field label={t("console.fiche.telephone")} htmlFor="fiche-telephone">
          <input
            id="fiche-telephone"
            value={telephone}
            onChange={(event) => {
              setTelephone(event.target.value);
            }}
            className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
            data-testid="fiche-telephone"
          />
        </Field>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => {
              void saveAddress();
            }}
            data-testid="fiche-adresse-enregistrer"
          >
            {t("common.action.enregistrer")}
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              setAddressError(null);
              setEditionAdresse(false);
            }}
            data-testid="fiche-adresse-annuler"
          >
            {t("common.action.annuler")}
          </Button>
        </div>
        {addressError === null ? null : (
          <p role="alert" className="text-sm text-rose-600" data-testid="fiche-adresse-erreur">
            {addressError}
          </p>
        )}
      </div>
    ) : (
      <div className="mb-4" data-testid="fiche-adresse">
        {adresse.ville === null ? (
          <p className="text-sm text-gray-500" data-testid="fiche-adresse-absente">
            {t("console.fiche.adresseACompleter")}
          </p>
        ) : (
          <>
            <p className="text-sm text-gray-900" data-testid="fiche-adresse-lue">
              {adresse.quartier} · {adresse.ville}
            </p>
            {adresse.telephone === null ? null : (
              <p className="text-sm text-gray-500" data-testid="fiche-adresse-telephone">
                {adresse.telephone}
              </p>
            )}
          </>
        )}
        <div className="mt-2">
          <Button
            variant="secondary"
            onClick={() => {
              // Opens on what is already there, so « Modifier » corrects rather
              // than re-types.
              setVille(adresse.ville ?? "");
              setQuartier(adresse.quartier ?? "");
              setTelephone(adresse.telephone ?? "");
              setAddressError(null);
              setEditionAdresse(true);
            }}
            data-testid="fiche-adresse-modifier"
          >
            {t("console.fiche.modifierAdresse")}
          </Button>
        </div>
      </div>
    );

  if (administrateur !== null) {
    return (
      <div data-testid="fiche-concession">
        <Card>
          <h1 className="text-lg font-medium text-gray-900">{concession.nom}</h1>
          <p className="mb-4 mt-1 text-sm text-gray-500">{t("console.fiche.introResponsable")}</p>
          {/* `P0-PTA-001`: where this dealership is. Rendered on BOTH branches ,
              a dealership without an administrator still has an address, and
              the founder's walk sets it before naming anybody. */}
          {blocAdresse}

          <p className="text-sm font-medium text-gray-900" data-testid="fiche-admin-nom-lu">
            {administrateur.fullName}
          </p>
          <p className="mt-1 text-sm text-gray-500" data-testid="fiche-admin-etat-lu">
            {stateLabel === null ? "" : t(stateLabel)}
          </p>

          {invitationEnCours === null ? null : (
            <div className="mt-4" data-testid="fiche-admin-renvoyer">
              <Button
                variant="secondary"
                onClick={() => {
                  void resendThisLink(invitationEnCours);
                }}
              >
                {t("console.fiche.renvoyer")}
              </Button>
            </div>
          )}

          {erreur === null ? null : (
            <p role="alert" className="mt-3 text-sm text-rose-600" data-testid="fiche-admin-erreur">
              {erreur}
            </p>
          )}
          {etat.phase === "envoye" ? (
            <p role="status" className="mt-3 text-sm text-gray-700" data-testid="fiche-admin-etat">
              {t("console.fiche.envoye")} {etat.email}
            </p>
          ) : null}
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="fiche-concession">
      <Card>
        <h1 className="text-lg font-medium text-gray-900">{concession.nom}</h1>
        <p className="mb-4 mt-1 text-sm text-gray-500">{t("console.fiche.intro")}</p>
        {/* `P0-PTA-001`: where this dealership is. Rendered on BOTH branches ,
              a dealership without an administrator still has an address, and
              the founder's walk sets it before naming anybody. */}
        {blocAdresse}

        <Field label={t("console.fiche.nom")} htmlFor="fiche-admin-nom">
          <input
            id="fiche-admin-nom"
            value={nom}
            onChange={(event) => {
              setNom(event.target.value);
            }}
            className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
            data-testid="fiche-admin-nom"
          />
        </Field>

        <Field label={t("console.fiche.email")} htmlFor="fiche-admin-email">
          <input
            id="fiche-admin-email"
            type="email"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
            }}
            className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
            data-testid="fiche-admin-email"
          />
        </Field>

        <div className="mt-4" data-testid="fiche-admin-envoyer">
          <Button
            onClick={() => {
              void nommer();
            }}
          >
            {t("console.fiche.nommer")}
          </Button>
        </div>

        {erreur === null ? null : (
          <p role="alert" className="mt-3 text-sm text-rose-600" data-testid="fiche-admin-erreur">
            {erreur}
          </p>
        )}

        {etat.phase === "repos" ? null : (
          <p role="status" className="mt-3 text-sm text-gray-700" data-testid="fiche-admin-etat">
            {etat.phase === "envoye"
              ? `${t("console.fiche.envoye")} ${etat.email}`
              : etat.phase === "enAttente"
                ? `${t("console.fiche.enAttente")} ${etat.email}`
                : `${t("console.fiche.nonEnvoye")} ${etat.email}`}
          </p>
        )}

        {/* `E-1`, `M2`: « Renvoyer » stays for `echec` AND for the row the job still owes. */}
        {etat.phase === "aRenvoyer" || etat.phase === "enAttente" ? (
          <div className="mt-3" data-testid="fiche-admin-renvoyer">
            <Button
              variant="secondary"
              onClick={() => {
                void resendThisLink(etat.invitationId);
              }}
            >
              {t("console.fiche.renvoyer")}
            </Button>
          </div>
        ) : null}
      </Card>
    </div>
  );
}
